import { ContentItemType, ItemType } from './ItemType';
import { Resource } from './Resource';
import { SequencedFlowStep } from './SequencedFlowStep';
import { MenuItem } from './MenuItem';
import { Hotspot } from './Hotspot';
import { Section } from './contentBuilder/Section';
import { StartingMessage } from './StartingMessage';
import { Item } from './Item';
import { Answer } from './Answer';
import { ResourcesFolder } from './ResourcesFolder';
import { BlindMapQuestion } from './BlindMapQuestion';
import { Quiz } from './Quiz';
import { SequencedFlowItem } from './SequencedFlowItem';
import { NonSequencedFlowItem } from './NonSequencedFlowItem';
import { Question } from './Question';
import { HotspotTab } from './HotspotTab';

export function isSequencedFlowItem(item: Item): item is SequencedFlowItem {
  return item.type === ItemType.SequencedFlow;
}

export function isNonSequencedFlowItem(item: Item): item is NonSequencedFlowItem {
  return item.type === ItemType.NonSequencedFlow;
}

export function isQuiz(item: Item): item is Quiz {
  return item.type === ItemType.Quiz;
}

export function isMenuItem(item: Item): item is MenuItem {
  return item.type === ItemType.MenuItem;
}

export function isResourcesFolder(item: Item): item is ResourcesFolder {
  return item.type === ItemType.ResourcesFolder;
}

export function isItem(item: Item): item is MainItem {
  return (
    isMenuItem(item) ||
    isQuiz(item) ||
    isSequencedFlowItem(item) ||
    isNonSequencedFlowItem(item) ||
    isResourcesFolder(item)
  );
}

export function isSection(item: Item): item is Section {
  return item.type === ItemType.Section;
}

export const isSectionOrSequencedFlow = (item: Item): item is Section | SequencedFlowItem => {
  return isSection(item) || isSequencedFlowItem(item);
};

export function isHotspot(item: Item): item is Hotspot {
  return item.type === ItemType.Hotspot;
}

export function isQuestion(item: Item): item is Question {
  return item.type === ItemType.Question;
}

export function isBlindMapQuestion(item: Item): item is BlindMapQuestion {
  return item.type === ItemType.BlindMapQuestion;
}

export function isHotspotTab(item: Item): item is HotspotTab {
  return item.type === ItemType.HotspotTab;
}

export function isSequencedFlowStep(item: Item): item is SequencedFlowStep {
  return item.type === ItemType.SequencedFlowStep;
}

export function isAnswer(item: Item): item is Answer {
  return item.type === ItemType.Answer;
}

export function isStartingMessage(item: Item): item is StartingMessage {
  return item.type === ItemType.StartingMessage;
}

export function isAutoProgressableItem(item: Item): item is Question | SequencedFlowStep {
  return isQuestion(item) || isSequencedFlowStep(item);
}

/** Represents content item types used as Test's children. */
export type TestChildrenTypes = BlindMapQuestion | Question;

export const isTestContentItem = (item: Item): item is TestChildrenTypes => {
  return isBlindMapQuestion(item) || isQuestion(item);
};

export type SequencedFlowChildrenTypes = Section | SequencedFlowStep;

export const isSequencedFlowContentItem = (item: Item): item is SequencedFlowChildrenTypes => {
  return isSection(item) || isSequencedFlowStep(item);
};

export function isResource(item: Item): item is Resource {
  return item.type === ItemType.Resource;
}

export function isContentItemType(item: Item): item is ContentItemType {
  return (
    isQuestion(item) ||
    isStartingMessage(item) ||
    isHotspotTab(item) ||
    isBlindMapQuestion(item) ||
    isSequencedFlowStep(item) ||
    isResource(item)
  );
}

export const isSequencedFlowSectionOrStep = (item: Item): item is Section | SequencedFlowStep =>
  isSection(item) || isSequencedFlowStep(item);

export const isItemWithContent = (
  item: Item
): item is Question | StartingMessage | HotspotTab | SequencedFlowStep | Resource =>
  isQuestion(item) || isStartingMessage(item) || isHotspotTab(item) || isSequencedFlowStep(item) || isResource(item);

export function ensureItemIsHotspot(item: Item): asserts item is Hotspot {
  if (!isHotspot(item)) {
    throw Error('Assertion failed: item is not hotspot.');
  }
}
export function ensureItemIsHotspotTab(item: Item): asserts item is HotspotTab {
  if (!isHotspotTab(item)) {
    throw Error('Assertion failed: item is not hotspot tab.');
  }
}

export function itemHasPosition(item: Item): item is Hotspot | BlindMapQuestion | SequencedFlowStep {
  return isHotspot(item) || isBlindMapQuestion(item) || isSequencedFlowStep(item);
}

export function ensureSection(item: Item): asserts item is Section {
  if (!isSection(item)) {
    throw Error('Expected item to be section');
  }
}

export function ensureQuestion(item: Item): asserts item is Question {
  if (!isQuestion(item)) {
    throw Error('Expected item to be question.');
  }
}

export function ensureStartingMessage(item: Item): asserts item is StartingMessage {
  if (!isStartingMessage(item)) {
    throw Error('Expected item to be starting message.');
  }
}

export function ensureSectionOrSequencedFlowItem(item: Item): asserts item is Section | SequencedFlowItem {
  if (!(isSection(item) || isSequencedFlowItem(item))) {
    throw Error('Expected item to be section or linear flow item');
  }
}

export function ensureSequencedFlowItem(item: Item): asserts item is SequencedFlowItem {
  if (!isSequencedFlowItem(item)) {
    throw Error('Expected item to be linear flow.');
  }
}

export function ensureNonSequencedFlowItem(item: Item): asserts item is NonSequencedFlowItem {
  if (!isNonSequencedFlowItem(item)) {
    throw Error('Expected item to be non linear flow.');
  }
}

export function ensureItemIsContentItemType(item: Item): asserts item is ContentItemType {
  if (!isContentItemType(item)) {
    throw Error('Expected item to be ContentItemType.');
  }
}

export type MainItem = MenuItem | Quiz | SequencedFlowItem | NonSequencedFlowItem | ResourcesFolder; // Temp naming

export function ensureIsItem(item: Item): asserts item is MainItem {
  if (!isItem(item)) {
    throw Error('Expected item to be a  MenuItem, Quiz, SequencedFlowItem, NonSequencedFlowItem or ResourcesFolder.');
  }
}

export function ensureIsMenuItem(item: Item): asserts item is MenuItem {
  if (!isMenuItem(item)) {
    throw Error('Expected item to be a  MenuItem.');
  }
}

export function ensureIsResourcesFolder(item: Item): asserts item is ResourcesFolder {
  if (!isResourcesFolder(item)) {
    throw Error('Expected item to be a Resource folder.');
  }
}

export function ensureIsResource(item: Item): asserts item is Resource {
  if (!isResource(item)) {
    throw Error('Expected item to be a Resource.');
  }
}

export function ensureIsQuiz(item: Item): asserts item is Quiz {
  if (!isQuiz(item)) {
    throw Error('Expected item to be a Quiz.');
  }
}

export function ensureBlindMapQuestion(item: Item): asserts item is BlindMapQuestion {
  if (!isBlindMapQuestion(item)) {
    throw Error('Expected item to be a BlindMapQuestion.');
  }
}

export function ensureSequencedFlowStep(item: Item): asserts item is SequencedFlowStep {
  if (!isSequencedFlowStep(item)) {
    throw Error('Expected item to be a step.');
  }
}

export function ensureNotUndefined<T>(i: T | undefined): asserts i is T {
  if (i === undefined) {
    throw Error('Expected value to not be undefined');
  }
}
